<template>
  <autocomplete
    ref="ac"
    :id="id || undefined"
    :placeholder="placeholder"
    :field_label="field_label"
    :no_filter="true"
    :options="search_label_options"
    :loading="search_label_loading"
    :value="boundValue"
    :disabled="disabled"
    :clear_on_select="clear_on_select"
    :prepend_icon="prepend_icon"
    :hide_no_data="labelSearchHideNoData"
    :no_data_text="labelSearchNoDataText"
    :hint="labelSearchHint"
    :persistent_hint="labelSearchHintPersistent"
    @updated_search_value="search_label_value_updated"
    @select="on_select"
    @cleared="on_cleared_inner"
  ></autocomplete>
</template>

<script>
import LoadSearchLabelsMixin from "@/mixins/LoadSearchLabelsMixin.vue";
import Autocomplete from "@/components/Autocomplete.vue";
import { recordLastUsedLabel } from "@/utils/lastUsedLabels";

export default {
  name: "SearchLabelAutocomplete",
  emits: ['label-selected', 'on_select', 'cleared', 'update:modelValue'],
  components: { Autocomplete },
  mixins: [LoadSearchLabelsMixin],
  props: {
    id: { default: null },
    label_type: { default: null },
    placeholder: { default: "Type to search" },
    modelValue: { default: null },
    value: { default: null },
    disabled: { type: Boolean, default: false },
    clear_on_select: { type: Boolean, default: false },
    search_label_limit: { type: Number, default: 50 },
    prepend_icon: { default: 'mdi-magnify' },
    /** v-text-field-style floating label (e.g. filter row next to meta contains). */
    field_label: { type: String, default: '' },
    /** When true, persist picks in localStorage and show them when the query is short. */
    track_last_used: { type: Boolean, default: true },
    /** Offer text_line in box search when the project has no such label yet (annotate OCR). */
    ensure_text_line_label: { type: Boolean, default: false },
    /** Offer Blackout in box search when the project has no such label yet. */
    ensure_blackout_label: { type: Boolean, default: false },
    allow_create_label: { type: Boolean, default: false },
    create_label_type: { type: Number, default: 1 },
  },
  computed: {
    boundValue() {
      return this.modelValue != null && this.modelValue !== ''
        ? this.modelValue
        : this.value;
    },
    labelSearchHideNoData() {
      const q = (this.labelSearchInput || '').trim();
      return q.length < this.labelSearchMinChars && !this.search_label_loading;
    },
    labelSearchNoDataText() {
      const q = (this.labelSearchInput || '').trim();
      if (q.length < this.labelSearchMinChars) {
        return this.$t('filter_tree.label_search_type_more');
      }
      if (this.search_label_loading) {
        return this.$t('filter_tree.label_search_loading');
      }
      return this.$t('filter_tree.label_search_no_matches');
    },
    labelSearchHint() {
      if (!this.search_label_loading) {
        return '';
      }
      return this.$t('filter_tree.label_search_loading');
    },
    labelSearchHintPersistent() {
      return this.search_label_loading;
    },
  },
  methods: {
    syncSelectedLabelFromValue() {
      const name = this.boundValue;
      if (!name || this.clear_on_select) {
        return;
      }
      if (this.search_label_selected && this.search_label_selected.name === name) {
        return;
      }
      let resolved = null;
      if (this.$store) {
        const row = (this.$store.state.label.items || []).find(
          (l) => l && l.name === name,
        );
        if (row) {
          resolved = {
            id: row.id,
            name: row.name,
            color: row.color,
            type: row.type,
          };
        }
      }
      if (!resolved) {
        resolved = { id: name, name };
      }
      this.search_label_selected = resolved;
      this.search_label_options = [resolved];
    },
    async on_select(label) {
      let resolved = label;
      if (label != null && (typeof label === 'string' || typeof label === 'number')) {
        resolved = (this.search_label_options || []).find((o) => o && o.id == label);
        if (!resolved && this.$store) {
          const row = (this.$store.state.label.items || []).find(
            (l) => l && (l.id == label || l.name === label),
          );
          if (row) {
            resolved = {
              id: row.id,
              name: row.name,
              color: row.color,
              type: row.type,
            };
          }
        }
      }
      if (!resolved || typeof resolved !== 'object' || resolved.disabled) {
        return;
      }
      if (resolved.id === '__create_label__' && resolved.createName) {
        await this.createProjectLabel(resolved);
        return;
      }
      label = resolved;
      if (this.track_last_used) {
        const projectId = typeof sessionStorage !== "undefined"
          ? sessionStorage.getItem("project_id")
          : null;
        recordLastUsedLabel(projectId, this.label_type, label);
      }
      this.search_label_selected = label;
      if (!this.clear_on_select) {
        this.search_label_options = [label];
      }
      this.$emit('update:modelValue', label.name);
      this.$emit('label-selected', label);
      this.$emit('on_select', label);
    },
    async createProjectLabel(option) {
      const name = String(option.createName || '').trim();
      if (!name) {
        return;
      }
      const LabelModel = (await import('@/models/label')).default;
      const labelType = option.type != null ? option.type : this.create_label_type;
      const created = await this.$api.label.add(
        new LabelModel(false, name, labelType, option.color || '0,99,133', false),
      );
      if (!created || !created.id) {
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('annotate.create_label_failed'));
        return;
      }
      await this.$store.dispatch('label/prefetch_all_background');
      this.$store.dispatch('snackbars/add_success_snack', {
        message: this.$t('annotate.create_label_success', { name }),
      });
      this.on_select({
        id: created.id,
        name: created.name,
        color: created.color,
        type: created.type,
      });
    },
    on_cleared_inner() {
      this.labelSearchSeq += 1;
      this.search_label_loading = false;
      this.search_label_selected = null;
      this.labelSearchInput = '';
      if (this.clear_on_select) {
        this.search_label_options = this.withEnsureTextLineOption(
          this.buildLastUsedSearchOptions(''),
          '',
        );
      } else if (this.labelsCatalogReady()) {
        this.search_label_options = this.withEnsureTextLineOption(
          this.mapStoreLabelsToOptions(this.filterStoreLabelsForSearch('')),
          '',
        );
      } else {
        this.search_label_options = this.withEnsureTextLineOption([], '');
      }
      this.$emit('update:modelValue', null);
      this.$emit('cleared');
    },
  },
  mounted() {
    this.syncSelectedLabelFromValue();
    if (this.track_last_used && this.clear_on_select) {
      this.search_label_options = this.withEnsureTextLineOption(
        this.buildLastUsedSearchOptions(''),
        '',
      );
    }
    this.$store.dispatch('label/prefetch_all_background').finally(() => {
      this.$nextTick(() => {
        this.search_label_value_updated(this.labelSearchInput || '');
      });
    });
  },
  watch: {
    boundValue() {
      this.syncSelectedLabelFromValue();
    },
    '$store.state.label.loaded'(loaded) {
      if (loaded) {
        this.$nextTick(() => {
          this.search_label_value_updated(this.labelSearchInput || '');
        });
      }
    },
    '$store.state.label.items.length'(len) {
      if (len > 0 && this.$store.getters['label/get_loaded']) {
        this.$nextTick(() => {
          this.search_label_value_updated(this.labelSearchInput || '');
        });
      }
    },
    'search_label_selected.name'(name) {
      if (name) {
        this.$emit('update:modelValue', name);
      }
    },
  },
};
</script>

<style scoped>
</style>
